import { useState, type ReactNode } from 'react'
import { Box, Menu, MenuItem, Typography } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { FS_SM, COLOR_PRIMARY, COLOR_PRIMARY_HOVER1, COLOR_PRIMARY_HOVER2 } from '../common/theme'
import { useCloseOnAnchorHidden } from '../common/useCloseOnAnchorHidden'
import type { CfpSelectOption } from '../types'

interface CfpSelectProps<T extends string | number = string> {
  value: T | ''
  options: CfpSelectOption<T>[]
  onChange: (value: T) => void
  // Optional small label rendered to the left of the pill (e.g. "Order by").
  label?: string
  // Shown in the trigger when nothing is selected.
  placeholder?: string
  // Adds a "-" option at the top that emits '' from onChange.
  clearable?: boolean
  disabled?: boolean
  // Make the trigger take the full available width (justify-between).
  fullWidth?: boolean
  // Optional minimum width for the dropdown menu, useful when option labels
  // are long (e.g. wrapped question text).
  menuMinWidth?: number
  // Optional max width for the trigger so very long labels truncate cleanly.
  triggerMaxWidth?: number
  // Drops the pill's own border + background-fill so the select can sit
  // visually inside another framed container (e.g. inside the TaWordLimit
  // pill). The chevron and hover-tint stay so it still reads as a control.
  borderless?: boolean
  testId?: string
}

// CFP-branded compact dropdown. Pill-shaped trigger with a subtle gold-tinted
// hover, rounded menu, and no MUI-Select underline weirdness. Use anywhere we
// previously had `<Select variant="standard">` in the toolbars.
export default function CfpSelect<T extends string | number = string>({
  value, options, onChange, label, placeholder, clearable, disabled, fullWidth,
  menuMinWidth, triggerMaxWidth, borderless, testId,
}: CfpSelectProps<T>) {
  // Track the trigger element in state so the Menu always sees an
  // anchor that's actually in the document layout. The previous
  // ref-based approach handed `anchorRef.current` to the Menu during
  // render, which is null on the first paint and goes stale if the
  // trigger is conditionally hidden — MUI then warns about an invalid
  // anchorEl. Setting `anchorEl` in the click handler couples open
  // state to the element 1:1.
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = !!anchorEl
  // Close when the trigger drops out of layout (e.g. tab switch hides
  // this whole subtree via `display: none`). Prevents MUI's "invalid
  // anchorEl" warning against a still-mounted-but-hidden anchor.
  useCloseOnAnchorHidden(anchorEl, () => setAnchorEl(null))

  const current = options.find(o => o.value === value)
  const triggerContent: ReactNode = current?.label ?? (placeholder ? placeholder : null)

  const handlePick = (v: T) => {
    onChange(v)
    setAnchorEl(null)
  }

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, ...(fullWidth && { width: '100%' }) }}>
      {label && (
        <Typography variant="caption" sx={{
          opacity: 0.6, fontSize: FS_SM, whiteSpace: 'nowrap', userSelect: 'none',
        }}>
          {label}
        </Typography>
      )}
      <Box
        data-testid={testId}
        data-cfp-select-open={open ? 'true' : 'false'}
        onClick={(e) => {
          if (disabled) return
          setAnchorEl(prev => prev ? null : e.currentTarget as HTMLElement)
        }}
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5,
          height: 24,
          px: borderless ? 0.5 : 1, pr: 0.25,
          borderRadius: 999,
          // Borderless mode - drop the frame + bg fill so the trigger reads
          // as part of the surrounding container. Keep the open-state tint
          // and hover so it's still clearly clickable.
          ...(borderless ? {
            border: 0,
            bgcolor: open ? COLOR_PRIMARY_HOVER1 : 'transparent',
            '&:hover': disabled ? undefined : { bgcolor: COLOR_PRIMARY_HOVER1 },
          } : {
            border: 1,
            borderColor: open ? COLOR_PRIMARY : 'divider',
            bgcolor: open ? COLOR_PRIMARY_HOVER1 : 'transparent',
            '&:hover': disabled ? undefined : {
              bgcolor: COLOR_PRIMARY_HOVER1,
              borderColor: COLOR_PRIMARY,
            },
          }),
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          userSelect: 'none',
          transition: 'background-color 0.12s, border-color 0.12s',
          ...(fullWidth && { width: '100%', justifyContent: 'space-between' }),
          ...(triggerMaxWidth ? { maxWidth: triggerMaxWidth } : {}),
        }}
      >
        <Box sx={{
          fontSize: FS_SM, fontWeight: 500,
          color: 'text.primary',
          opacity: triggerContent ? 1 : 0.5,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          minWidth: 0, flex: fullWidth ? 1 : '0 1 auto',
        }}>
          {triggerContent ?? '-'}
        </Box>
        <KeyboardArrowDownIcon sx={{
          fontSize: 16,
          opacity: 0.65,
          flexShrink: 0,
          transform: open ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.15s',
        }} />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              minWidth: menuMinWidth ?? 140,
              borderRadius: 1.5,
              border: 1,
              borderColor: 'divider',
              boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
              py: 0.25,
            },
          },
        }}
      >
        {clearable && (
          <MenuItem
            onClick={() => handlePick('' as T)}
            sx={{ fontSize: FS_SM, py: 0.5, px: 1.25, opacity: 0.55 }}
          >
            -
          </MenuItem>
        )}
        {options.map(opt => (
          <MenuItem
            key={String(opt.value)}
            selected={opt.value === value}
            onClick={() => handlePick(opt.value)}
            sx={{
              fontSize: FS_SM, py: 0.5, px: 1.25, gap: 0.75,
              // Layout: label flexes to fill, hint sits right-aligned in grey.
              display: 'flex', alignItems: 'center',
              '&.Mui-selected': {
                bgcolor: COLOR_PRIMARY_HOVER2,
                fontWeight: 600,
                '&:hover': { bgcolor: COLOR_PRIMARY_HOVER2 },
              },
              '&:hover': { bgcolor: COLOR_PRIMARY_HOVER1 },
            }}
          >
            <Box component="span" sx={{ flex: 1, minWidth: 0 }}>{opt.label}</Box>
            {opt.hint != null && (
              <Box component="span" sx={{ ml: 1, opacity: 0.55, fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                {opt.hint}
              </Box>
            )}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}
